/**
 * SBN v1 字符串 ⇄ 谜题。
 * 依据 docs/sbn-v1-format.md 第 2、3、7 节实现。
 *
 * 玩家标注段（标志位 e/f）与历史段当前不解析，仅原样保留在返回值里，
 * 待存档续玩阶段接入。
 */

import {
    codeToDim, dimToCode, isValidFlag, borderSizes, FLAG_NONE,
} from './alphabet.js';
import { decodeRegions, encodeRegions } from './regionBorders.js';

/**
 * @typedef {object} SbnPuzzle
 * @property {number} dim 棋盘边长
 * @property {number} stars 每行/列/区的星数
 * @property {string} flag 标志位
 * @property {number[][]} regionGrid 区域 id 网格（id 从 1 起）
 * @property {string} annotation 玩家标注段原文（无则空串）
 * @property {string} history 历史段原文，不含前导 `~`（无则空串）
 */

/**
 * 判断字符串是否为合法 SBN v1。不抛异常，用于在解码前筛选。
 * @param {string} sbn
 * @returns {boolean}
 */
export function isSbnV1(sbn) {
    if (typeof sbn !== 'string' || sbn.length < 4) return false;

    const dim = codeToDim(sbn.slice(0, 2));
    if (dim < 0) return false;
    if (sbn[2] < '0' || sbn[2] > '9') return false;
    if (!isValidFlag(sbn[3])) return false;

    return sbn.length - 4 >= borderSizes(dim).chars;
}

/**
 * 解码 SBN v1 字符串。
 * @param {string} sbn
 * @returns {SbnPuzzle}
 * @throws {SyntaxError} 格式非法
 */
export function decodeSbn(sbn) {
    if (typeof sbn !== 'string' || sbn.length < 4) {
        throw new SyntaxError(`SBN 过短: '${sbn}'`);
    }

    const dim = codeToDim(sbn.slice(0, 2));
    if (dim < 0) {
        throw new SyntaxError(
            `尺寸码非法: '${sbn.slice(0, 2)}'（两字符须相同且 dim 落在 4..25）`);
    }

    const stars = sbn.charCodeAt(2) - 48; // '0'
    if (stars < 0 || stars > 9) {
        throw new SyntaxError(`星数非法: '${sbn[2]}'`);
    }

    const flag = sbn[3];
    if (!isValidFlag(flag)) {
        throw new SyntaxError(`标志位非法: '${flag}'`);
    }

    const { chars: borderChars } = borderSizes(dim);
    if (sbn.length - 4 < borderChars) {
        throw new SyntaxError(
            `边界数据不足: 需 ${borderChars} 字符，实得 ${sbn.length - 4}`);
    }

    const regionGrid = decodeRegions(sbn.substr(4, borderChars), dim);

    // 尾部：标注段 [~ 历史段]
    let annotation = '';
    let history = '';
    if (flag !== FLAG_NONE) {
        const tail = sbn.slice(4 + borderChars);
        const tilde = tail.indexOf('~');
        if (tilde >= 0) {
            annotation = tail.slice(0, tilde);
            history = tail.slice(tilde + 1);
        } else {
            annotation = tail;
        }
    }

    return { dim, stars, flag, regionGrid, annotation, history };
}

/**
 * 编码为 SBN v1 字符串（仅谜题定义，标志位固定 `W`）。
 * @param {number[][]} regionGrid
 * @param {number} stars
 * @returns {string}
 */
export function encodeSbn(regionGrid, stars) {
    if (!Number.isInteger(stars) || stars < 0 || stars > 9) {
        throw new RangeError(`星数须为 0..9 的整数: ${stars}`);
    }
    const dim = regionGrid.length;
    return dimToCode(dim) + String(stars) + FLAG_NONE + encodeRegions(regionGrid);
}

/**
 * 统计区域数量。N 星谜题要求区域数等于 dim。
 * @param {number[][]} regionGrid
 * @returns {number}
 */
export function countRegions(regionGrid) {
    let max = 0;
    for (const row of regionGrid) for (const id of row) if (id > max) max = id;
    return max;
}
